'use client';

import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import { CURRENCY_CODE } from '../lib/constants';
import type { Money } from '../lib/types';
import type { SanityImageProps } from '../sanity/utils/fragments/image-fragment';
import { useShopifyCart } from './hooks/use-shopify-cart';
import type {
  ContextCart,
  ContextCartItem,
  Product,
  ProductVariant,
} from './types';

export type SanityCartVariant = Omit<ContextCartItem, 'id' | 'quantity'>;

type UpdateType = 'plus' | 'minus' | 'delete';

type CartAction =
  | {
      type: 'UPDATE_ITEM';
      payload: {
        merchandiseId: string;
        updateType: UpdateType;
      };
    }
  | {
      type: 'ADD_ITEM';
      payload: {
        variant: ProductVariant;
        product: Product;
        quantity?: number;
      };
    }
  | { type: 'SET_CART'; payload: ContextCart };

type CartContextType = {
  open: boolean;
  setOpen: (open: boolean) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  activeItemQuantity: number;
  setActiveItemQuantity: (quantity: number) => void;
  cart: ContextCart | undefined;
  updateCartItem: ({
    merchandiseId,
    updateType,
  }: {
    merchandiseId: string;
    updateType: UpdateType;
  }) => boolean;
  addCartItem: ({
    product,
    variant,
    quantity,
    squareMetersPerBox,
    wastageMultiplier,
    maxQuantityAvailable,
  }: {
    product: Product;
    variant: ProductVariant;
    quantity?: number;
    squareMetersPerBox?: number | null;
    wastageMultiplier?: number;
    maxQuantityAvailable?: number;
  }) => boolean;
};

const CartContext = createContext<CartContextType | undefined>(undefined);

function calculateItemCost(quantity: number, price: number): number {
  return price * quantity;
}

function validateCartRules(items: ContextCartItem[]): {
  isValid: boolean;
  error?: string;
} {
  if (items.length === 0) {
    return { isValid: true };
  }

  const sampleItems = items.filter((item) => item.category === 'SAMPLE');
  const nonSampleItems = items.filter((item) => item.category !== 'SAMPLE');

  // Rule 1: Can't mix SAMPLE and non-SAMPLE products
  if (sampleItems.length > 0 && nonSampleItems.length > 0) {
    return {
      isValid: false,
      error: 'Cannot mix sample products with regular products in cart',
    };
  }

  // Rule 2: Maximum 10 SAMPLE products in cart
  if (sampleItems.length > 0) {
    const totalSampleQuantity = sampleItems.reduce(
      (sum, item) => sum + item.quantity,
      0
    );

    if (totalSampleQuantity > 10) {
      return {
        isValid: false,
        error: 'Maximum 10 sample products allowed in cart',
      };
    }
  }

  return { isValid: true };
}

function validateInventoryLimits({
  items,
  targetItem,
  maxQuantityAvailable,
  squareMetersPerBox,
  wastageMultiplier = 1,
}: {
  items: ContextCartItem[];
  targetItem: ContextCartItem;
  maxQuantityAvailable: number;
  squareMetersPerBox?: number | null;
  wastageMultiplier?: number;
}): {
  isValid: boolean;
  error?: string;
} {
  // Skip validation if backorder is allowed
  if (targetItem.allowBackorder) {
    return { isValid: true };
  }

  const currentQuantityInCart = items
    .filter((item) => item.variantId === targetItem.variantId)
    .reduce((sum, item) => sum + item.quantity, 0);

  const availableToAdd = Math.max(
    0,
    maxQuantityAvailable - currentQuantityInCart
  );

  // Check if the target item's quantity exceeds what's available
  if (targetItem.quantity > maxQuantityAvailable) {
    const isTile = targetItem.category === 'TILE';
    let error: string;

    if (isTile && squareMetersPerBox) {
      const availableArea =
        (availableToAdd * squareMetersPerBox) / wastageMultiplier;
      error = `Only ${availableArea.toFixed(2)} m² available to add to cart. For orders exceeding this amount, please contact sales.`;
    } else {
      error = `Only ${availableToAdd} item${availableToAdd === 1 ? '' : 's'} available to add to cart.`;
    }

    return {
      isValid: false,
      error,
    };
  }

  return { isValid: true };
}

function updateCartItemData({
  item,
  updateType,
}: {
  item: ContextCartItem;
  updateType: UpdateType;
}): ContextCartItem | null {
  if (updateType === 'delete') {
    return null;
  }

  const newQuantity =
    updateType === 'plus' ? item.quantity + 1 : item.quantity - 1;
  if (newQuantity === 0) {
    return null;
  }

  const singleItemAmount = Number(item.price.amount) / item.quantity;

  const newTotalAmount = calculateItemCost(newQuantity, singleItemAmount);

  const newOriginalPrice = newTotalAmount;

  return {
    ...item,
    quantity: newQuantity,
    price: {
      amount: newTotalAmount.toFixed(2),
      currencyCode: CURRENCY_CODE,
    },
    originalPrice: {
      amount: newOriginalPrice.toFixed(2),
      currencyCode: CURRENCY_CODE,
    },
  };
}

function createOrUpdateCartItem({
  existingItem,
  variant,
  product,
  quantity,
}: {
  existingItem: ContextCartItem | undefined;
  variant: ProductVariant;
  product: Product;
  quantity?: number;
}): ContextCartItem {
  const correctQuantity = existingItem
    ? existingItem.quantity + 1
    : quantity || 1;
  const totalAmount = calculateItemCost(correctQuantity, variant.price);

  // Use compareAtPrice for original price if it's greater than the regular price
  const singleItemOriginalPrice =
    variant.compareAtPrice && variant.compareAtPrice > variant.price
      ? variant.compareAtPrice
      : variant.price;
  const originalPrice = calculateItemCost(
    correctQuantity,
    singleItemOriginalPrice
  );

  // Construct SanityImageProps for the optimistic update
  const optimisticImage: SanityImageProps = {
    asset: {
      _ref: product.image, // Use the product image URL
      altText: product.title, // Use product title as alt text
      metadata: null, // No metadata available optimistically
    },
    crop: null,
    hotspot: null,
  };

  return {
    id: existingItem?.id ?? variant.gid, // Use variant.id if existingItem.id is undefined
    category: product.category,
    squareMetersPerBox: product.squareMetersPerBox,
    quantity: correctQuantity,
    price: {
      amount: totalAmount.toFixed(0),
      currencyCode: CURRENCY_CODE,
    },
    originalPrice: {
      amount: originalPrice.toFixed(0),
      currencyCode: CURRENCY_CODE,
    },
    title: product.title,
    variantId: variant.gid,
    option1: variant.option1,
    option2: variant.option2,
    option3: variant.option3,
    image: optimisticImage, // Assign the constructed SanityImageProps object
    productId: product.gid,
    productTitle: product.title,
    allowBackorder: product.allowBackorder,
    preOrderTag: product.preOrderTag,
  };
}

function updateCartTotals(
  lines: ContextCartItem[]
): Pick<ContextCart, 'totalQuantity' | 'total'> {
  const totalQuantity = lines.reduce((sum, item) => sum + item.quantity, 0);
  const totalAmount = getCartTotalFromLines(lines);

  return {
    totalQuantity,
    total: { amount: totalAmount.toString(), currencyCode: CURRENCY_CODE },
  };
}

function cartReducer(state: ContextCart, action: CartAction): ContextCart {
  switch (action.type) {
    case 'UPDATE_ITEM': {
      const { merchandiseId, updateType } = action.payload;

      if (updateType === 'delete') {
        const updatedLines = state.items.filter(
          (item) => item.id !== merchandiseId
        );
        return {
          ...state,
          ...updateCartTotals(updatedLines),
          items: updatedLines,
        };
      }

      const updatedLines = state.items
        .map((item) =>
          item.variantId === merchandiseId
            ? updateCartItemData({ item, updateType })
            : item
        )
        .filter(Boolean) as ContextCartItem[];

      if (updatedLines.length === 0) {
        return {
          ...state,
          items: [],
          totalQuantity: 0,
          total: { amount: '0', currencyCode: CURRENCY_CODE },
        };
      }

      return {
        ...state,
        ...updateCartTotals(updatedLines),
        items: updatedLines,
      };
    }
    case 'ADD_ITEM': {
      const { variant, product, quantity = 1 } = action.payload;

      const existingItem = state.items.find(
        (item) => item.variantId === variant.gid
      );

      const updatedItem = createOrUpdateCartItem({
        existingItem,
        variant,
        product,
        quantity,
      });

      const updatedLines = existingItem
        ? state.items.map((item) =>
            item.variantId === variant.gid ? updatedItem : item
          )
        : [updatedItem, ...state.items];

      return {
        ...state,
        ...updateCartTotals(updatedLines),
        items: updatedLines,
      };
    }
    case 'SET_CART':
      return action.payload;
    default:
      return state;
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const { data } = useShopifyCart();
  const [optimisticCart, dispatch] = useReducer(
    cartReducer,
    data?.sanityCart as ContextCart
  );
  const lastValidationResult = useRef<boolean>(true);

  useEffect(() => {
    if (data) {
      dispatch({ type: 'SET_CART', payload: data.sanityCart });
    }
  }, [data]);

  const updateCartItem = ({
    merchandiseId,
    updateType,
  }: {
    merchandiseId: string;
    updateType: UpdateType;
  }): boolean => {
    // Pre-validate the operation
    if (updateType === 'delete') {
      lastValidationResult.current = true;
    } else {
      const updatedLines = optimisticCart.items
        .map((item) =>
          item.variantId === merchandiseId
            ? updateCartItemData({ item, updateType })
            : item
        )
        .filter(Boolean) as ContextCartItem[];

      // Basic cart rules validation (samples, etc.)
      const cartRulesValidation = validateCartRules(updatedLines);
      if (!cartRulesValidation.isValid) {
        lastValidationResult.current = false;
        if (cartRulesValidation.error) {
          toast.error(cartRulesValidation.error);
        }
        return false;
      }

      lastValidationResult.current = true;
    }

    dispatch({
      type: 'UPDATE_ITEM',
      payload: { merchandiseId, updateType },
    });
    return lastValidationResult.current;
  };

  const addCartItem = ({
    variant,
    product,
    quantity,
    squareMetersPerBox,
    wastageMultiplier = 1,
    maxQuantityAvailable,
  }: {
    variant: ProductVariant;
    product: Product;
    quantity?: number;
    squareMetersPerBox?: number | null;
    wastageMultiplier?: number;
    maxQuantityAvailable?: number;
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
  }): boolean => {
    // Pre-validate the operation
    const existingItem = optimisticCart.items.find(
      (item) => item.variantId === variant.gid
    );

    const updatedItem = createOrUpdateCartItem({
      existingItem,
      variant,
      product,
      quantity,
    });

    const updatedLines = existingItem
      ? optimisticCart.items.map((item) =>
          item.variantId === variant.gid ? updatedItem : item
        )
      : [updatedItem, ...optimisticCart.items];

    // Basic cart rules validation (samples, etc.)
    const cartRulesValidation = validateCartRules(updatedLines);
    if (!cartRulesValidation.isValid) {
      lastValidationResult.current = false;
      if (cartRulesValidation.error) {
        toast.error(cartRulesValidation.error);
      }
      return false;
    }

    // Inventory validation if maxQuantityAvailable is provided
    if (maxQuantityAvailable !== undefined) {
      const inventoryValidation = validateInventoryLimits({
        items: updatedLines,
        targetItem: updatedItem,
        maxQuantityAvailable,
        squareMetersPerBox,
        wastageMultiplier,
      });

      if (!inventoryValidation.isValid) {
        lastValidationResult.current = false;
        if (inventoryValidation.error) {
          toast.error(inventoryValidation.error);
        }
        return false;
      }
    }

    lastValidationResult.current = true;

    dispatch({
      type: 'ADD_ITEM',
      payload: {
        variant,
        product,
        quantity: quantity || 1,
      },
    });
    return true;
  };

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeItemQuantity, setActiveItemQuantity] = useState(1);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const value = useMemo(
    () => ({
      cart: optimisticCart,
      updateCartItem,
      addCartItem,
      open,
      setOpen,
      loading,
      setLoading,
      activeItemQuantity,
      setActiveItemQuantity,
    }),
    [
      optimisticCart,
      open,
      setOpen,
      loading,
      setLoading,
      activeItemQuantity,
      setActiveItemQuantity,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}

export function getCartTotalFromLines(lines: ContextCartItem[]): Money {
  const totalAmount = lines.reduce(
    (sum, item) => sum + Number(item.price.amount),
    0
  );
  return { amount: totalAmount.toFixed(2), currencyCode: CURRENCY_CODE };
}

export function useCartTotals() {
  const { cart } = useCart();
  const total = getCartTotalFromLines(cart?.items || []);
  return total;
}
